
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function CatalogPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    dealershipType: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/catalog-download', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setShowSuccess(true);
        // Trigger catalog download
        const link = document.createElement('a');
        link.href = '/catalog/glamping-dealership-catalog.pdf';
        link.download = 'Glamping-Dealership-Catalog-2024.pdf';
        link.click();
      } else {
        alert('There was an error processing your request. Please try again.');
      }
    } catch (error) {
      alert('There was an error processing your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-download-cloud-2-line text-2xl text-emerald-600"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Catalog Download Started!</h2>
            <p className="text-lg text-gray-600 mb-8">
              Your comprehensive dealership catalog is downloading now. Check your downloads folder for the complete product guide.
            </p>
            <div className="space-y-4">
              <Link
                to="/dealership"
                className="inline-block bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Back to Dealership
              </Link>
              <div className="text-sm text-gray-500">
                <p>Didn't receive the download? <button onClick={() => window.location.reload()} className="text-emerald-600 hover:underline cursor-pointer">Try again</button></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Download Dealership Catalog
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get instant access to our comprehensive product catalog featuring geo domes, tiny homes, safari tents, and complete dealership packages
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-8 lg:p-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What's Inside the Catalog</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-emerald-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Complete Product Line</h4>
                    <p className="text-gray-600 text-sm">Detailed specifications for geo domes, tiny homes, safari tents, and glamping pods</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-emerald-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Investment Packages</h4>
                    <p className="text-gray-600 text-sm">Detailed breakdown of Starter, Growth, and Premium dealership options</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-emerald-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Technology Solutions</h4>
                    <p className="text-gray-600 text-sm">POS systems, booking platforms, and solar power specifications</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-emerald-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Marketing Materials</h4>
                    <p className="text-gray-600 text-sm">Sample campaigns, branding guidelines, and promotional strategies</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-emerald-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">ROI Projections</h4>
                    <p className="text-gray-600 text-sm">Financial models and return on investment calculations</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-8 lg:p-12">
              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="catalog-download">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    Company/Organization
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="dealershipType" className="block text-sm font-medium text-gray-700 mb-2">
                    Interested Dealership Package
                  </label>
                  <select
                    id="dealershipType"
                    name="dealershipType"
                    value={formData.dealershipType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
                  >
                    <option value="">Select package type</option>
                    <option value="starter">Starter Dealership - $50,000</option>
                    <option value="growth">Growth Dealership - $100,000</option>
                    <option value="premium">Premium Dealership - $200,000</option>
                    <option value="exploring">Just exploring options</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Information
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
                    placeholder="Tell us about your business goals or specific questions..."
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? 'Processing...' : 'Download Catalog'}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  By downloading, you agree to receive follow-up communications about our dealership opportunities.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
